// The GIF picker. Lifted from the Andon app; GIFs stay in the merge.
//
// A grid of previews behind a search box, and picking one puts it in the tray
// as an ordinary attachment — same as a photo. That is the whole design: a GIF
// is not a special kind of message, it is a picture somebody chose faster.
//
// NOTHING HERE TALKS TO GIPHY. The search goes to the BFF, which holds the key,
// and every preview is served from this origin. The app's CSP allows no
// third-party origin at all and this feature was not worth widening it for —
// see the BFF's andon/giphy.ts.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { andonApi } from '@/api/andon/client';
import { Icon } from '@/components/ui/Icon';
import { Sheet } from '@/components/ui/Sheet';
import { useT } from '@/i18n/useT';
import { andonKeys } from '@/queries/andon';

/**
 * How long to sit on a keystroke before searching.
 *
 * Every search is an outbound call this server makes on somebody's behalf, so
 * per-keystroke would be a request per letter against a third-party quota. 350ms
 * is about a typing pause and short enough not to feel held up.
 */
const DEBOUNCE_MS = 350;

export function GifPicker({
  open,
  onClose,
  onPick,
  busy,
  seed,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (gifId: string, title: string) => void;
  busy: boolean;
  /**
   * What to search before anybody types — read from the conversation, or from
   * the situation the andon is in. See lib/gifSeed.ts for why it is never the
   * SUBJECT of the andon.
   */
  seed: string;
}) {
  const t = useT();
  /**
   * THE BOX STAYS EMPTY. The seed is what gets SEARCHED, not what gets shown.
   *
   * Prefilling read as though somebody had typed it: a word you did not choose
   * sitting in a box you are about to use, which has to be cleared before you
   * can search for anything else. The grid answers the question "is there
   * anything here" on its own — the box does not need to explain it.
   *
   * So three pieces of state rather than two. `term` is what the person has
   * typed; `query` is what is actually being searched; `touched` is how the
   * two are told apart, because an empty box means "nothing typed yet" on open
   * and "cleared it deliberately" afterwards, and those want opposite answers.
   */
  const [term, setTerm] = useState('');
  const [touched, setTouched] = useState(false);
  const [query, setQuery] = useState(seed);

  useEffect(() => {
    if (!touched) return;
    const id = setTimeout(() => setQuery(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [term, touched]);

  // Reset on close: the previous search is not what the next opening should
  // walk into. Re-seeding on CLOSE rather than on open is what makes the next
  // opening different — `seed` is re-rolled by the caller from the
  // conversation as it stands then.
  useEffect(() => {
    if (!open) {
      setTerm('');
      setTouched(false);
      setQuery(seed);
    }
  }, [open, seed]);

  const {
    data: found = [],
    isFetching,
    isError,
  } = useQuery({
    queryKey: andonKeys.gifs(query),
    queryFn: () => andonApi.gifs(query),
    enabled: open && query.length > 0,
    staleTime: 300_000,
    retry: 0,
  });

  // Nothing on screen once the box is empty. react-query keeps the last
  // `data` when a query goes disabled, so clearing the box would leave the
  // previous term's grid sitting there under a box that no longer said what
  // they were.
  const gifs = query.length > 0 ? found : [];

  const empty = useMemo(() => open && query.length > 0 && !isFetching && !isError && gifs.length === 0, [open, query, isFetching, isError, gifs.length]);

  return (
    <Sheet open={open} onClose={onClose} variant="tall" title={t.gifAddT}>
      <input
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setTouched(true);
        }}
        data-autofocus
        placeholder={t.gifSearchPhT}
        aria-label={t.gifSearchPhT}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          height: 44,
          padding: '0 14px',
          borderRadius: 13,
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          color: 'var(--ink)',
          font: '500 13px Manrope,sans-serif',
          outline: 'none',
        }}
      />

      <p style={{ margin: '8px 2px 12px', font: '500 10.5px/1.5 Manrope,sans-serif', color: 'var(--sub)' }}>
        {/* Said plainly, because this is a workplace and somebody should know
            the search is filtered before they try. */}
        {t.gifRatedT}
      </p>

      {isError && (
        <p role="status" style={{ margin: '0 0 12px', font: '500 12px Manrope,sans-serif', color: 'var(--danger)' }}>
          {t.gifNotAnsweringT}
        </p>
      )}

      {empty && (
        <p role="status" style={{ margin: 0, font: '500 12px Manrope,sans-serif', color: 'var(--sub)' }}>
          {t.gifNothingForT} “{query}”.
        </p>
      )}

      {query.length === 0 && <p style={{ margin: 0, font: '500 12px Manrope,sans-serif', color: 'var(--sub)' }}>{t.gifTypeToSearchT}</p>}

      <div
        style={{
          display: 'grid',
          // Two columns on a phone, more as the sheet gets wider.
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 8,
          opacity: busy ? 0.5 : 1,
          pointerEvents: busy ? 'none' : undefined,
        }}
      >
        {gifs.map((gif) => (
          <button
            key={gif.id}
            type="button"
            disabled={busy}
            onClick={() => onPick(gif.id, gif.title)}
            aria-label={gif.title ? `${t.gifSendT} ${gif.title}` : t.gifSendThisT}
            style={{
              padding: 0,
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              borderRadius: 12,
              overflow: 'hidden',
              cursor: 'pointer',
              lineHeight: 0,
              // A GIF's own aspect ratio varies wildly; a fixed box keeps the
              // grid from becoming a ragged column of different heights.
              aspectRatio: '4 / 3',
            }}
          >
            <img src={andonApi.gifPreviewUrl(gif.id)} alt={gif.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </button>
        ))}
      </div>

      {isFetching && (
        <p role="status" style={{ margin: '12px 0 0', font: '500 11px Manrope,sans-serif', color: 'var(--sub)' }}>
          <Icon name="search" size={12} /> {t.gifSearchingT}
        </p>
      )}
    </Sheet>
  );
}
